import * as ACT from './actions'
import { createReducer } from 'redux-create-reducer'
import update from 'immutability-helper'
import { getTaskCategory, fromBoardItems } from 'utils/serializers'
import { categoryMapper, getFirstTaskDueDate } from 'utils/helpers'
import { toBoardData } from './serializers'
import { getLaneIndex, getPaginatedBoardIndex, getPaginatedBopardUnfilteredIndex } from './utils'

const initialState = {
  rowData: [],
  templates: [],
  paginatedBoard: { lanes: [] }
}

const proposalsReducer = createReducer(initialState, {
  [ACT.PROPOSALS_ROW_DATA_FETCH_SUCCESS](state, action) {
    const { rowData } = action.payload
    return {
      ...state,
      rowData
    }
  },
  [ACT.PROPOSAL_FETCH_SUCCESS](state, action) {
    const { proposal } = action.payload
    const index = state.rowData.length ? state.rowData.findIndex(p => p.id === proposal.id) : 0
    return update(state, {
      rowData: {
        [index]: { $set: proposal }
      }
    })
  },
  [ACT.PROPOSALS_BOARD_ROW_DATA_FETCH_SUCCESS](state, action) {
    const { rowData, paginatedBoard } = action.payload
    return {
      ...state,
      rowData,
      paginatedBoard
    }
  },
  [ACT.PROPOSAL_UPDATE_STATUS_SUCCESS](state, action) {
    const { id, oldStatus, status, proposals } = action.payload
    const index = state.rowData[oldStatus].findIndex(p => +p.id === +id)
    const proposal = proposals[status].find(p => +p.id === +id)

    const laneOldIndex = getLaneIndex(state.paginatedBoard, oldStatus)
    const laneNewIndex = getLaneIndex(state.paginatedBoard, status)
    const proposalIndex = getPaginatedBoardIndex(state.paginatedBoard, laneOldIndex, id)
    const unfilteredProposalIndex = getPaginatedBopardUnfilteredIndex(
      state.paginatedBoard,
      laneOldIndex,
      id
    )

    const newState = update(state, {
      rowData: {
        [oldStatus]: { $splice: [[index, 1]] },
        [status]: { $unshift: [proposal] }
      },
      paginatedBoard: {
        lanes: {
          [laneOldIndex]: {
            cards: { $splice: [[proposalIndex, 1]] },
            unfilteredCards: { $splice: [[unfilteredProposalIndex, 1]] }
          },
          [laneNewIndex]: {
            cards: { $unshift: [proposal] },
            unfilteredCards: { $unshift: [proposal] }
          }
        }
      }
    })
    return newState
  },
  [ACT.CREATE_PROPOSAL_BOARD_TASK_SUCCESS](state, action) {
    const { status, proposals, id } = action.payload
    const laneIndex = getLaneIndex(state.paginatedBoard, status)
    const paginatedBoardIndex = getPaginatedBoardIndex(state.paginatedBoard, laneIndex, id)
    const proposal = proposals[status].find(p => +p.id === +id)

    return update(state, {
      rowData: {
        [status]: { $set: proposals[status] }
      },
      paginatedBoard: {
        lanes: {
          [laneIndex]: {
            cards: {
              [paginatedBoardIndex]: { $set: proposal }
            }
          }
        }
      }
    })
  },
  [ACT.UPDATE_BOARD_PROPOSAL_TASK_SUCCESS](state, action) {
    const { status, proposal } = action.payload
    const index = state.rowData[status].findIndex(p => +p.id === proposal.id)
    const laneIndex = getLaneIndex(state.paginatedBoard, status)
    const paginatedBoardIndex = getPaginatedBoardIndex(state.paginatedBoard, laneIndex, proposal.id)

    return update(state, {
      rowData: {
        [status]: {
          [index]: { $set: proposal }
        }
      },
      paginatedBoard: {
        lanes: {
          [laneIndex]: {
            cards: {
              [paginatedBoardIndex]: { $set: proposal }
            }
          }
        }
      }
    })
  },
  [ACT.DELETE_BOARD_PROPOSAL_TASK_SUCCESS](state, action) {
    const { status, proposal } = action.payload
    const index = state.rowData[status].findIndex(p => +p.id === proposal.id)
    const laneIndex = getLaneIndex(state.paginatedBoard, status)
    const paginatedBoardIndex = getPaginatedBoardIndex(state.paginatedBoard, laneIndex, proposal.id)

    return update(state, {
      rowData: {
        [status]: {
          [index]: { $set: proposal }
        }
      },
      paginatedBoard: {
        lanes: {
          [laneIndex]: {
            cards: {
              [paginatedBoardIndex]: { $set: proposal }
            }
          }
        }
      }
    })
  },
  [ACT.PROPOSAL_BOARD_TASK_MARK_COMPLETED_SUCCESS](state, action) {
    const { proposalId, proposalStatus, id } = action.payload
    const tasks = state.rowData[proposalStatus]

    if (!tasks) return state

    const proposalIndex = tasks.findIndex(p => p.id === proposalId)
    const laneIndex = getLaneIndex(state.paginatedBoard, proposalStatus)
    const paginatedBoardIndex = getPaginatedBoardIndex(state.paginatedBoard, laneIndex, proposalId)

    let taskIndex
    let category

    for (const key in categoryMapper) {
      const index = tasks[proposalIndex][categoryMapper[key]].findIndex(t => t.id === id)
      if (~index) {
        taskIndex = index
        category = key
        break
      }
    }

    const task = tasks[proposalIndex][categoryMapper[category]][taskIndex]
    const date = getFirstTaskDueDate(tasks[proposalIndex], task)

    return update(state, {
      rowData: {
        [proposalStatus]: {
          [proposalIndex]: {
            [categoryMapper[category]]: { $splice: [[taskIndex, 1]] },
            completedTasks: { $push: [task] },
            firstTaskDueDate: { $set: date }
          }
        }
      },
      paginatedBoard: {
        lanes: {
          [laneIndex]: {
            cards: {
              [paginatedBoardIndex]: {
                [categoryMapper[category]]: { $splice: [[taskIndex, 1]] },
                completedTasks: { $push: [task] },
                firstTaskDueDate: { $set: date }
              }
            }
          }
        }
      }
    })
  },
  [ACT.PROPOSAL_BOARD_TASK_MARK_NOT_COMPLETED_SUCCESS](state, action) {
    const { proposalId, proposalStatus, id } = action.payload
    const tasks = state.rowData[proposalStatus]

    if (!tasks) return state

    const proposalIndex = tasks.findIndex(p => p.id === proposalId)
    const laneIndex = getLaneIndex(state.paginatedBoard, proposalStatus)
    const paginatedBoardIndex = getPaginatedBoardIndex(state.paginatedBoard, laneIndex, proposalId)

    const taskIndex = tasks[proposalIndex].completedTasks.findIndex(t => t.id === id)
    const task = tasks[proposalIndex].completedTasks[taskIndex]
    task.dateCompleted = null

    const category = getTaskCategory(task)

    let date = getFirstTaskDueDate(tasks[proposalIndex], task)
    date = task.dueDate < date ? task.dueDate : date

    return update(state, {
      rowData: {
        [proposalStatus]: {
          [proposalIndex]: {
            completedTasks: { $splice: [[taskIndex, 1]] },
            [categoryMapper[category]]: { $push: [task] },
            firstTaskDueDate: { $set: date }
          }
        }
      },
      paginatedBoard: {
        lanes: {
          [laneIndex]: {
            cards: {
              [paginatedBoardIndex]: {
                completedTasks: { $splice: [[taskIndex, 1]] },
                [categoryMapper[category]]: { $push: [task] },
                firstTaskDueDate: { $set: date }
              }
            }
          }
        }
      }
    })
  },
  [ACT.PROPOSALS_TEMPLATES_FETCH_SUCCESS](state, action) {
    const { templates } = action.payload
    return {
      ...state,
      templates
    }
  },
  [ACT.UPDATE_PROPOSAL_PAGINATION_BOARD](state, action) {
    const { pagination } = action.payload

    return update(state, {
      paginatedBoard: { $set: toBoardData(fromBoardItems(state.rowData), pagination) }
    })
  }
})

export default proposalsReducer
